// Platform UI language — powered by i18next/react-i18next.
//
// applyPlatformLanguage()/storedPlatformLanguage() keep the same signatures and
// per-institute localStorage namespacing as before, so existing callers
// (App.tsx, Settings.tsx, AccessPermissions.tsx) don't need to change. Under
// the hood this now drives i18next's active language plus <html> dir/lang,
// instead of a DOM-substitution/MutationObserver hack.
//
// Locale content lives in frontend/src/locales/<language>/<namespace>.json,
// loaded lazily per language via Vite's import.meta.glob (one chunk per
// language, no separate HTTP round trip). Use useTranslation() from
// react-i18next in components to render translated strings.

import i18n, { type BackendModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { PLATFORM_LANGUAGES } from '@/lib/languages';

const NAMESPACES = ['common', 'errors'] as const;

// Statically analyzed by Vite at build time; the returned object's keys are
// resolved file paths, which we then look up dynamically at runtime.
const localeModules = import.meta.glob('../locales/*/*.json') as Record<
    string,
    () => Promise<{ default: Record<string, string> }>
>;

async function loadNamespace(lang: string, ns: string): Promise<Record<string, string>> {
    const loader = localeModules[`../locales/${lang}/${ns}.json`];
    if (!loader) return {};
    const mod = await loader();
    return mod.default ?? (mod as unknown as Record<string, string>);
}

const dynamicBackend: BackendModule = {
    type: 'backend',
    init() { /* no-op — loadNamespace reads directly from the glob map */ },
    read(language, namespace, callback) {
        loadNamespace(language, namespace)
            .then((data) => callback(null, data))
            .catch((err) => callback(err, null));
    },
};

// i18n.init() is async and, left un-awaited, races with the very first
// changeLanguage() call fired from applyPlatformLanguage() at app boot —
// init's own default-language setup can resolve after that call and silently
// clobber it back to fallbackLng. applyPlatformLanguage() awaits this before
// calling changeLanguage() so that first call always wins.
const i18nReady = i18n
    .use(dynamicBackend)
    .use(initReactI18next)
    .init({
        fallbackLng: 'english',
        supportedLngs: PLATFORM_LANGUAGES.map((l) => l.code),
        ns: NAMESPACES,
        defaultNS: 'common',
        interpolation: { escapeValue: false }, // React already escapes
        react: { useSuspense: false },
        partialBundledLanguages: true,
    });

export default i18n;

// Namespaced by institute ID so switching institutes on the same browser/kiosk
// machine never leaks one institute's language choice into another's — a
// bare, un-namespaced key would let institute B inherit whatever language
// institute A last selected on that device.
function storageKey(instituteId?: string | null): string {
    return instituteId ? `platform_language:${instituteId}` : 'platform_language';
}

/** Apply the platform UI language (+ <html> dir/lang). Superadmin is always English. */
export function applyPlatformLanguage(lang: string, role?: string, instituteId?: string | null) {
    const code = role === 'super_admin' ? 'english' : String(lang || 'english').toLowerCase();
    try { localStorage.setItem(storageKey(instituteId), code); } catch { /* ignore */ }

    void i18nReady.then(() => i18n.changeLanguage(code));

    const meta = PLATFORM_LANGUAGES.find((l) => l.code === code);
    document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
}

export function storedPlatformLanguage(instituteId?: string | null): string {
    try { return localStorage.getItem(storageKey(instituteId)) || 'english'; } catch { return 'english'; }
}
